import type {RouterState} from 'connected-react-router'
import {connectRouter} from 'connected-react-router'
import type {History} from 'history'
import {enableMapSet} from 'immer'
import * as Redux from 'redux'
import type {AuthState} from '~/redux/reducers/auth'
import authReducer from '~/redux/reducers/auth'
import type {PastGamePositionsState} from '~/redux/reducers/pastGamePositions'
import pastGamePositionsReducer from '~/redux/reducers/pastGamePositions'
import type {AuthenticatedPlayerState} from './authenticatedPlayer'
import authenticatedPlayerReducer from './authenticatedPlayer'
import type {SettingsState} from './settings'
import settingsReducer from './settings'
import type {SpectatorState} from './spectator'
import spectatorReducer from './spectator'
import type {ToastState} from './toast'
import toastReducer from './toast'
import type {UIState} from './ui'
import uiReducer from './ui'

// stops redux from complaining about our use of Map in our stores
// https://github.com/reduxjs/redux-toolkit/issues/466
// https://stackoverflow.com/a/67220847/2544629
enableMapSet()

export type ReduxState = {
  router: RouterState // this has to have the key `router` for `connected-react-router` to work, iirc.

  authState: AuthState
  pastGamePositionsState: PastGamePositionsState
  authenticatedPlayerState: AuthenticatedPlayerState
  settingsState: SettingsState
  spectatorState: SpectatorState
  toastState: ToastState
  uiState: UIState
}

const createReducers = (history: History) => ({
  router: connectRouter(history),

  authState: authReducer,
  pastGamePositionsState: pastGamePositionsReducer,
  authenticatedPlayerState: authenticatedPlayerReducer,
  settingsState: settingsReducer,
  spectatorState: spectatorReducer,
  toastState: toastReducer,
  uiState: uiReducer,
})

const createAppReducer = (history: History) =>
  Redux.combineReducers(createReducers(history))

export const ROOT_LOGOUT_ACTION_KEY = '_ROOT_LOGOUT'

export const createRootReducer =
  (history: History) =>
  (
    state: ReduxState | undefined,
    action: any // Redux.AnyAction
  ) => {
    if (action.type === ROOT_LOGOUT_ACTION_KEY) {
      // in the case of logout we want to wipe out all the state, except the routing state.
      // This is achieved here by passing in no state to the combineReducers above
      // idea from https://stackoverflow.com/questions/35622588/how-to-reset-the-state-of-a-redux-store
      return createAppReducer(history)({router: state?.router} as any, action)
    }

    return createAppReducer(history)(state, action)
  }
